import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from '@tanstack/react-router';
import axios from 'axios';
import { useAppSelector } from '@/store/hooks';
import { setAuthInfo } from '@/store/slices/authSlice';
import { determineNextOnboardingRoute } from '@/utils/navigation';
import { getStatsigClient } from '@/utils/statsig';

export const useAuthBasedNavigation = () => {
  const navigate = useNavigate();
  const authState = useAppSelector((state) => state.auth);
  const statsigClient = getStatsigClient();
  const lastRoute = useRef<string | null>(null);

  const navigateBasedOnAuthState = useCallback(() => {
    const nextRoute = determineNextOnboardingRoute(authState, statsigClient);
    if (nextRoute !== lastRoute.current) {
      console.log(`Navigating from ${window.location.pathname} to ${nextRoute}`);
      navigate({ to: nextRoute, replace: true });
      lastRoute.current = nextRoute;
    }
  }, [authState, navigate, lastRoute.current]);

  useEffect(() => {
    navigateBasedOnAuthState();
  }, [authState]);
};

export const useOnboardingCompletionNavigation = () => {
  const navigate = useNavigate();
  const authState = useAppSelector((state) => state.auth);
  const dispatch = useDispatch();

  useEffect(() => {
    const navigateToAskeeOrSelf = async () => {
      if (authState.onboardingRewardUserId) {
        const userRes = await axios.post('/api/getAskeeUser', {
          userId: authState.onboardingRewardUserId,
        });
        if (userRes.data.user) {
          navigate({
            to: '/profile/$uniqId',
            params: { uniqId: userRes.data.user.uniqId },
          });
        }
      } else {
        navigate({ to: '/' });
      }
      dispatch(setAuthInfo({ justCompletedOnboardingTutorial: false }));
    };

    if (authState.justCompletedOnboardingTutorial) {
      navigateToAskeeOrSelf();
    }
  }, [authState.justCompletedOnboardingTutorial, navigate, authState.onboardingRewardUserId]);
};
